// Pagination: prev/next, page indicator, page-size selector.
// Single source of truth for current page lives in useDataTable's reducer.

import type { JSX } from "react";
import type { DataTableState } from "./types.js";
import { DEFAULT_PAGE_SIZE_OPTIONS } from "./types.js";

const NAV_BTN = "text-sm px-3 py-1.5 rounded-lg border border-slate-300 bg-white hover:bg-slate-50 disabled:opacity-40 disabled:cursor-not-allowed transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-navy-500";
const SELECT_CLS = "text-sm rounded-lg border border-slate-300 bg-white px-2 py-1 focus:outline-none focus-visible:ring-2 focus-visible:ring-navy-500";

interface Props {
  state: DataTableState;
  total: number;
  pageSizeOptions?: readonly number[];
}

function totalPagesOf(total: number, limit: number): number {
  if (total <= 0) return 1;
  return Math.max(1, Math.ceil(total / limit));
}

function rangeOf(page: number, limit: number, total: number): { from: number; to: number } {
  if (total <= 0) return { from: 0, to: 0 };
  const from = (page - 1) * limit + 1;
  const to = Math.min(page * limit, total);
  return { from, to };
}

function PageSizeSelect({ state, options }: { state: DataTableState; options: readonly number[] }): JSX.Element {
  return (
    <label className="flex items-center gap-2 text-sm text-slate-600">
      <span>Rows</span>
      <select className={SELECT_CLS} value={state.query.limit}
        onChange={(e) => state.setLimit(Number(e.target.value))}
        aria-label="Rows per page">
        {options.map((opt) => <option key={opt} value={opt}>{opt}</option>)}
      </select>
    </label>
  );
}

function PageNav({ state, totalPages }: { state: DataTableState; totalPages: number }): JSX.Element {
  const page = state.query.page;
  return (
    <div className="flex items-center gap-2">
      <button type="button" className={NAV_BTN} onClick={() => state.setPage(page - 1)}
        disabled={page <= 1} aria-label="Previous page">Prev</button>
      <span className="text-sm text-slate-600" aria-current="page">
        Page <strong>{page}</strong> of {totalPages}
      </span>
      <button type="button" className={NAV_BTN} onClick={() => state.setPage(page + 1)}
        disabled={page >= totalPages} aria-label="Next page">Next</button>
    </div>
  );
}

export function DataTablePagination({ state, total, pageSizeOptions }: Props): JSX.Element {
  const options = pageSizeOptions ?? DEFAULT_PAGE_SIZE_OPTIONS;
  const totalPages = totalPagesOf(total, state.query.limit);
  const { from, to } = rangeOf(state.query.page, state.query.limit, total);
  return (
    <div className="flex flex-wrap items-center justify-between gap-3 mt-3 pt-3 border-t border-slate-100">
      <div className="text-sm text-slate-500" aria-live="polite">
        {total === 0 ? "0 results" : <>Showing <strong>{from}</strong>–<strong>{to}</strong> of <strong>{total}</strong></>}
      </div>
      <div className="flex items-center gap-4">
        <PageSizeSelect state={state} options={options} />
        <PageNav state={state} totalPages={totalPages} />
      </div>
    </div>
  );
}
